import type { Action } from './strategy.ts';
import type { JupiterJournal } from './venues/jupiter-journal.ts';
/**
 * Shared domain model.
 *
 * Two independent funds run side by side on the same engine:
 *
 *   short  Shortcoin ($SHORT)  creator fees open SHORT positions on BTC/ETH/SOL
 *   long   Longcoin  ($LONG)   creator fees open LONG  positions on BTC/ETH/SOL
 *
 * Each fund has its own pump.fun token, its own creator-fee stream, its own
 * treasury and its own burn ledger. They never share capital: a drawdown in one
 * cannot touch the other. Both follow the same rule on a realized win --
 * 25% buys and burns that fund's own token, 75% goes back to work.
 *
 * All monetary amounts are USD unless the field name says otherwise.
 * All timestamps are epoch milliseconds (UTC).
 */

export type MarketSymbol = 'BTC' | 'ETH' | 'SOL';

export const MARKET_SYMBOLS: readonly MarketSymbol[] = ['BTC', 'ETH', 'SOL'] as const;

export type Side = 'long' | 'short';

/** A fund is identified by the side it takes. */
export type FundId = Side;

export const FUND_IDS: readonly FundId[] = ['short', 'long'] as const;

export type PriceSource = 'binance' | 'coinbase' | 'kraken' | 'coingecko';

export interface SourceQuote {
  source: PriceSource;
  price: number;
}

export interface PriceQuote {
  symbol: MarketSymbol;
  /** Median of all healthy sources. */
  price: number;
  /** Individual source prices that contributed, for the transparency panel. */
  sources: SourceQuote[];
  /** Max relative deviation between contributing sources, e.g. 0.004 = 0.4%. */
  dispersion: number;
  ts: number;
}

export type PriceBook = Record<MarketSymbol, PriceQuote>;

/** A single laddered entry. Positions are built from 1..maxLadderSteps of these. */
export interface Tranche {
  /** Entry costs already removed from cash; included once in realized PnL. */
  entryFeeUsd?: number;
  entryBorrowFeeUsd?: number;
  id: string;
  symbol: MarketSymbol;
  side: Side;
  entryPrice: number;
  /** Margin posted for this tranche. */
  collateralUsd: number;
  leverage: number;
  /** collateralUsd * leverage */
  notionalUsd: number;
  /** Base-asset quantity: notionalUsd / entryPrice. Always positive. */
  qty: number;
  /** Trigger price that cuts the loss. Above entry for shorts, below for longs. */
  stopPrice: number;
  /** Trigger price that banks the win. Below entry for shorts, above for longs. */
  takeProfitPrice: number;
  /** True once price moved far enough in our favour to pull the stop to breakeven. */
  trailArmed: boolean;
  /** 0-indexed rung of the ladder this tranche represents. */
  ladderStep: number;
  openedAt: number;
  /** Cumulative funding. Positive means the venue paid us to hold this. */
  fundingAccruedUsd: number;
  /**
   * Venue ORDER IDS for the resting reduce-only triggers, as accepted by the
   * venue's cancel call. Never a transaction signature. `orderId` is absent when
   * the entry was a market order that filled without resting.
   */
  venue: {
    positionId?: string;
    orderId?: string;
    takeProfitOrderId?: string;
    stopOrderId?: string;
  };
}

export type CloseReason = 'take_profit' | 'stop_loss' | 'trail_stop' | 'manual' | 'liquidation';

export interface ClosedTranche {
  id: string;
  symbol: MarketSymbol;
  side: Side;
  entryPrice: number;
  exitPrice: number;
  collateralUsd: number;
  leverage: number;
  notionalUsd: number;
  qty: number;
  /** Net of fees and inclusive of funding. Negative for losses. */
  realizedPnlUsd: number;
  feesUsd: number;
  fundingAccruedUsd: number;
  reason: CloseReason;
  openedAt: number;
  closedAt: number;
}

export interface Treasury {
  /**
   * Idle capital: everything not currently posted as margin.
   *
   * There is no separate reserve balance. The reserve is simply the slice of
   * this that the strategy refuses to deploy, enforced by `deployRatio` when
   * sizing tranches, so the two can never drift out of sync.
   */
  freeCollateralUsd: number;
  /** Burn credit earned but still below `minBurnUsd`, carried to a later tick. */
  pendingBurnUsd: number;
  /** Highest cumulative realized PnL seen, for high-water-mark accounting. */
  realizedPnlHighWater: number;
  /**
   * Wallet lamports at the end of the last creator-fee claim.
   *
   * Live mode only. Anything above this that turns up later -- typically a sweep
   * that confirmed after the engine stopped waiting on it -- is credited on a
   * subsequent tick, exactly once. Null until the first live claim.
   */
  solBaselineLamports?: number | null;
}

export interface Lifetime {
  creatorFeesClaimedUsd: number;
  creatorFeesClaimedSol: number;
  realizedPnlUsd: number;
  fundingUsd: number;
  tradingFeesUsd: number;
  /** USD spent buying the token on the open market in order to burn it. */
  burnedUsd: number;
  /** Token units actually destroyed. */
  tokensBurned: number;
  redeployedUsd: number;
  wins: number;
  losses: number;
}

export interface BurnRecord {
  id: string;
  fund: FundId;
  ts: number;
  usdSpent: number;
  tokensBurned: number;
  /** Token price implied by the buy, in USD. */
  avgPriceUsd: number;
  buyTxSig?: string;
  burnTxSig?: string;
  simulated: boolean;
}

export type EventKind =
  | 'boot'
  | 'tick'
  | 'fee_claim'
  | 'entry'
  | 'ladder_add'
  | 'take_profit'
  | 'stop_loss'
  | 'trail_armed'
  | 'funding'
  | 'burn'
  | 'redeploy'
  | 'risk_block'
  | 'error';

export interface LedgerEvent {
  id: string;
  fund: FundId;
  ts: number;
  kind: EventKind;
  symbol?: MarketSymbol;
  message: string;
  /** Signed USD impact, when the event moves money. */
  usd?: number;
  txSig?: string;
  meta?: Record<string, unknown>;
}

/** One point on a fund's public equity curve. */
export interface EquityPoint {
  ts: number;
  /** freeCollateral + margin in open tranches + unrealized PnL */
  equityUsd: number;
  burnedUsd: number;
  realizedPnlUsd: number;
}

/** Everything that belongs to one fund. */
export interface FundState {
  pendingAction?: Action;
  pendingPrices?: PriceBook;
  jupiterJournal?: JupiterJournal;
  burnJournal?: JupiterJournal;
  id: FundId;
  treasury: Treasury;
  lifetime: Lifetime;
  openTranches: Tranche[];
  /** Most recent closes, newest first, capped. */
  closedTranches: ClosedTranche[];
  burns: BurnRecord[];
  events: LedgerEvent[];
  equityCurve: EquityPoint[];
  /**
   * Last observed cumulative funding level per market, so a stateless tick can
   * book the delta rather than re-counting the whole balance.
   */
  fundingLevels: Partial<Record<MarketSymbol, number>>;
  /** Wall-clock of the last creator-fee sweep, used to size paper fee inflow. */
  lastFeeClaimAt: number;
  /** Set when this fund deliberately refused to open new positions. */
  halt?: { reason: string; since: number } | null;
}

export interface EngineState {
  venue?: 'drift' | 'jupiter';
  schemaVersion: number;
  strategyVersion: string;
  mode: 'paper' | 'live';
  bootedAt: number;
  lastTickAt: number;
  tickCount: number;
  funds: Record<FundId, FundState>;
  /** Last successful price read, shared by both funds. */
  lastPrices?: PriceBook;
}

// ---------------------------------------------------------------------------
// Strategy configuration shape
//
// Lives here rather than in config.ts so the browser can render the live
// parameters via a type-only import, with no access to server-side code.
// ---------------------------------------------------------------------------

export interface MarketParams {
  /** Share of deployable capital allocated to this market. Weights sum to 1. */
  weight: number;
  leverage: number;
  /** Adverse move from average entry that closes the position. */
  stopPct: number;
  /** Favourable move from average entry that banks the position. */
  takeProfitPct: number;
  /** Favourable move after which the stop is pulled to breakeven. */
  trailArmPct: number;
  /** Adverse move from average entry that adds the next ladder rung. */
  ladderSpacingPct: number;
  /** Rungs the ladder may hold. Allocation is divided evenly across them. */
  maxLadderSteps: number;
  /**
   * Smallest order the venue accepts, in base units, and also its step size.
   *
   * Optional base-quantity floor; Jupiter uses the $10 collateral floor instead.
   */
  minOrderBase: number;
  /**
   * Assumed annualised funding for this side, used only in paper mode.
   * Negative represents borrowing costs. Jupiter charges both sides.
   */
  paperFundingApr: number;
}

/** Parameters for one fund. */
export interface FundConfig {
  id: FundId;
  side: Side;
  /** Display name, e.g. "Shortcoin". */
  name: string;
  /** Ticker without the dollar sign, e.g. "SHORT". */
  symbol: string;
  /** One-line description of the thesis. */
  tagline: string;
  /** Path to the logo under /logos. */
  logo: string;
  markets: Record<MarketSymbol, MarketParams>;
  /** Fraction of treasury that may be used as margin. Remainder is reserve. */
  deployRatio: number;
  /** Wait this long after a stop-out before re-entering the same market. */
  reentryCooldownMs: number;
}

export interface StrategyConfig {
  version: string;
  funds: Record<FundId, FundConfig>;
  profitSplit: {
    /** Share of realized profit used to buy and burn the token. */
    burn: number;
    /** Share of realized profit returned to the trading portfolio. */
    redeploy: number;
  };
  /**
   * 'per_trade'       burn on every profitable close (louder, more frequent burns)
   * 'high_water_mark' burn only when cumulative realized PnL sets a new high
   *                   (never burns while the portfolio is underwater)
   */
  profitAccounting: 'per_trade' | 'high_water_mark';
  /** Don't open a tranche smaller than this; venue minimums make it pointless. */
  minTrancheUsd: number;
  /** Let burn credit accumulate until it clears this, to avoid dust swaps. */
  minBurnUsd: number;
  /** Never burn if it would take a fund's equity below this floor. */
  minTreasuryUsd: number;
  /** Refuse to trade if price sources disagree by more than this. */
  maxPriceDispersion: number;
  /** Refuse to trade on a price read older than this. */
  maxPriceAgeMs: number;
  /** Minimum independent price sources required to trade. */
  minPriceSources: number;
  /** Round-trip taker fee assumption, as a fraction of notional. */
  takerFeeRate: number;
  /** Maintenance margin fraction used for the liquidation estimate. */
  maintenanceMarginRate: number;
  /** Retention caps so the persisted blob stays small. */
  retain: { events: number; closes: number; equityPoints: number; burns: number };
}

// ---------------------------------------------------------------------------
// Derived view models (computed, never persisted)
// ---------------------------------------------------------------------------

export interface PositionView {
  symbol: MarketSymbol;
  side: Side;
  trancheCount: number;
  maxLadderSteps: number;
  avgEntryPrice: number;
  markPrice: number;
  collateralUsd: number;
  notionalUsd: number;
  qty: number;
  leverage: number;
  unrealizedPnlUsd: number;
  /** Unrealized PnL as a fraction of posted collateral. */
  unrealizedPnlPct: number;
  stopPrice: number;
  takeProfitPrice: number;
  /** True when the stop has been pulled to breakeven. */
  trailArmed: boolean;
  /** Progress from average entry toward the take-profit target, 0..1. */
  progressToTarget: number;
  /** Fraction of the way from average entry to liquidation, 0..1. */
  liquidationRisk: number;
  liquidationPrice: number;
  fundingAccruedUsd: number;
}

/** Public view of one fund. */
export interface FundSnapshot {
  id: FundId;
  side: Side;
  name: string;
  symbol: string;
  tagline: string;
  logo: string;
  config: FundConfig;
  halt: FundState['halt'];
  equityUsd: number;
  deployedCollateralUsd: number;
  freeCollateralUsd: number;
  reserveUsd: number;
  unrealizedPnlUsd: number;
  pendingBurnUsd: number;
  positions: PositionView[];
  positionSyncPending?: boolean;
  /** Hard risk limits in force for this fund. */
  limits: {
    /** Ceiling on posted margin, in USD. */
    maxDeployedUsd: number;
    /** Posted margin as a fraction of that ceiling, 0..1. */
    deployedPctOfCap: number;
    /** True once the cap is leaving no room for another rung. */
    atCap: boolean;
  };
  lifetime: Lifetime;
  winRate: number | null;
  burns: BurnRecord[];
  recentCloses: ClosedTranche[];
  events: LedgerEvent[];
  equityCurve: EquityPoint[];
  token: {
    mint: string | null;
    symbol: string;
    name: string;
    /** Supply burned as a fraction of the 1B pump.fun launch supply. */
    burnedSupplyPct: number | null;
    totalSupply: number;
  };
}

export interface PublicSnapshot {
  backendRevision?: string;
  mode: 'paper' | 'live';
  /** True when the env kill switch is halting every new entry across both funds. */
  killSwitch: boolean;
  strategyVersion: string;
  strategy: StrategyConfig;
  now: number;
  lastTickAt: number;
  tickCount: number;
  prices: PriceBook | null;
  funds: Record<FundId, FundSnapshot>;
  /** Both funds added together, for the headline numbers. */
  combined: {
    equityUsd: number;
    unrealizedPnlUsd: number;
    realizedPnlUsd: number;
    burnedUsd: number;
    creatorFeesClaimedUsd: number;
    fundingUsd: number;
    openPositions: number;
    wins: number;
    losses: number;
    winRate: number | null;
  };
  /** Interleaved event feed across both funds, newest first. */
  events: LedgerEvent[];
}
